/**
 * pedidoBuilder.js
 *
 * Funciones puras para construir y mapear el objeto pedido (BAPI format).
 * Extraídas de preparePedido para hacer la lógica más modular y testeable.
 */

// ─────────────────────────────────────────────
// Utilidades de formateo
// ─────────────────────────────────────────────

/**
 * Comprueba si un valor es vacío/nulo/undefined
 */
function isEmpty(value) {
  return value === null || value === undefined || value === "";
}

/**
 * Procesa un valor numérico con signo (+/-).
 * Devuelve { value, valueWithSign, sign } o null si el valor está vacío.
 *
 * Usado para ESFERA y CILINDRO.
 */
export function processSignedValue(rawValue) {
  if (isEmpty(rawValue)) {
    return null;
  }

  const str = String(rawValue);
  const numericValue = parseFloat(str.replace(",", "."));

  if (isNaN(numericValue)) {
    return null;
  }

  const sign = Math.sign(numericValue) >= 0 ? "+" : "-";
  const absoluteValue = Math.abs(numericValue);

  return {
    value: `${absoluteValue}`,
    valueWithSign: `${sign}${absoluteValue}`,
    sign,
  };
}

// ─────────────────────────────────────────────
// Mapeo de datos de ojo (OD / OI)
// ─────────────────────────────────────────────

/**
 * Extrae un ojo de `oClaseBAPI` por tipo ("D" o "I") mirando solo `listOJOIN`.
 *
 * Para *escribir* (preparePedido) es lo que se quiere. Para *leer* usa
 * `getOjoIn`, que además cubre los pedidos que solo traen los accesores
 * `ojoInDerecho` / `ojoInIzquierdo`.
 */
export function extractEye(bapi, tipo) {
  const ojos = bapi.listOJOIN?.clsZES_OJO_INPUT || [];
  const ojo = ojos.find((o) => o.ZOJO === tipo);
  return ojo ? { ...ojo } : {};
}

/**
 * Devuelve el ojo de entrada de un BAPI por tipo ("D" = OD / "I" = OI).
 *
 * `listOJOIN.clsZES_OJO_INPUT` es la fuente canónica: es lo que escribe
 * `mountEyesIntoPedido` en cada `preparePedido`, o sea lo que refleja el
 * formulario en curso. Los accesores `ojoInDerecho`/`ojoInIzquierdo` solo los
 * puebla la respuesta del servidor y `mountEyesIntoPedido` los sincroniza
 * únicamente *si ya existen*, así que en un pedido nuevo (todavía sin calcular)
 * no están o se quedan desfasados. Por eso: lista primero, accesor como
 * fallback.
 *
 * @param {Object} bapi - `pedido.oClaseBAPI`
 * @param {"D"|"I"} tipo
 * @returns {Object} copia del ojo, o `{}` si el pedido no trae datos de ese ojo
 */
export function getOjoIn(bapi, tipo) {
  if (!bapi) return {};
  const ojo = extractEye(bapi, tipo);
  if (Object.keys(ojo).length > 0) return ojo;
  // Copia también en esta rama: quien lee un ojo nunca debe poder mutar el pedido.
  return { ...(tipo === "D" ? bapi.ojoInDerecho : bapi.ojoInIzquierdo) };
}

/**
 * Mapea los datos de graduación de un ojo al formato BAPI.
 *
 * @param {Object} eyeState - Estado del ojo (state.od o state.oi)
 * @param {Object} ojoIn - Objeto BAPI del ojo (se modifica in-place)
 */
export function mapGraduacionToBAPI(eyeState, ojoIn) {
  if (!eyeState) return;

  // Esfera
  const esfera = processSignedValue(eyeState.esfera);
  if (esfera) {
    ojoIn.ESFERA = esfera.value;
    ojoIn.ESFERA_CON_SIGNO = esfera.valueWithSign;
    ojoIn.SIGNO_ESF = esfera.sign;
  } else {
    ojoIn.ESFERA = "";
    ojoIn.ESFERA_CON_SIGNO = "";
    ojoIn.SIGNO_ESF = "";
  }

  // Cilindro
  const cilindro = processSignedValue(eyeState.cilindro);
  if (cilindro) {
    ojoIn.CILINDRO = cilindro.value;
    ojoIn.CILINDRO_CON_SIGNO = cilindro.valueWithSign;
    ojoIn.SIGNO_CIL = cilindro.sign;
  } else {
    ojoIn.CILINDRO = "";
    ojoIn.CILINDRO_CON_SIGNO = "";
    ojoIn.SIGNO_CIL = "";
  }

  // Eje
  ojoIn.EJE = !isEmpty(eyeState.eje) ? String(eyeState.eje) : "";

  // Adición
  ojoIn.ADICION = eyeState.adicion || "";

  // Diámetro: último valor puesto (estándar o especial) es el que se envía
  const diametroValue = eyeState.diametro ?? eyeState.diametroEspecial ?? null;
  if (diametroValue !== null && diametroValue !== undefined) {
    ojoIn.DIAMETRO = diametroValue;
  }

  // Stock e informativo
  ojoIn.SOLO_STOCK = eyeState.stock ? "X" : "";
  ojoIn.SOLO_INFOR = eyeState.informativo ? "X" : "";
}

// ─────────────────────────────────────────────
// Mapeo de datos compartidos (ambos ojos)
// ─────────────────────────────────────────────

/**
 * Mapea campos compartidos (coloración, fotocromatico, tratamiento, etc.)
 * a ambos ojos del pedido.
 *
 * @param {Object} state - Estado del módulo pedidos
 * @param {Object} rootState - Estado raíz del store
 * @param {Object} ojoInDch - Ojo derecho BAPI
 * @param {Object} ojoInIzq - Ojo izquierdo BAPI
 */
export function mapSharedFieldsToBAPI(state, rootState, ojoInDch, ojoInIzq) {
  // Campos que se aplican igual a ambos ojos
  const sharedMappings = [
    { source: state.coloracion, field: "COLORACION" },
    { source: state.fotocromatico, field: "FOTOCROMICO" },
    { source: state.color, field: "COLOR" },
    { source: state.tono, field: "TONO" },
    { source: state.focomaterial, field: "FOCO_MATERIAL" },
    { source: state.lente, field: "JER_EDI_INET" },
  ];

  sharedMappings.forEach(({ source, field }) => {
    if (source !== null && source !== undefined) {
      ojoInDch[field] = source;
      ojoInIzq[field] = source;
    }
  });

  // Índice (prioritario desde ofertas)
  if (rootState.ofertas?.indice !== null && rootState.ofertas?.indice !== undefined) {
    ojoInDch.IND_REF = rootState.ofertas.indice;
    ojoInIzq.IND_REF = rootState.ofertas.indice;
  } else if (state.caracteristicasLente?.Indice) {
    ojoInDch.IND_REF = state.caracteristicasLente.Indice;
    ojoInIzq.IND_REF = state.caracteristicasLente.Indice;
  }

  // Tratamiento (prioritario desde state, fallback caracteristicasLente)
  if (state.tratamiento !== null && state.tratamiento !== undefined) {
    ojoInDch.TRATAM1 = state.tratamiento;
    ojoInIzq.TRATAM1 = state.tratamiento;
  } else if (state.caracteristicasLente?.Tratamiento) {
    ojoInDch.TRATAM1 = state.caracteristicasLente.Tratamiento;
    ojoInIzq.TRATAM1 = state.caracteristicasLente.Tratamiento;
  }

  // Cruz de montaje
  if (state.caracteristicasLente?.CruzMontaje) {
    const pos = state.caracteristicasLente.CruzMontaje.split(";");
    ojoInDch.Cruz_posX = pos[0];
    ojoInDch.Cruz_posY = pos[0];
    ojoInIzq.Cruz_posX = pos[0];
    ojoInIzq.Cruz_posY = pos[0];
  }
}

// ─────────────────────────────────────────────
// Mapeo de adicionales
// ─────────────────────────────────────────────

/**
 * Retira de ambos ojos del pedido los campos que aportaba un juego de datos
 * adicionales. Contrapunto de `mapAdicionalesToBAPI`: aquél solo escribe los
 * `BAPI_CAMPO` presentes en el array actual y nunca limpia los que dejan de
 * aplicar, así que al cambiar de lente un adicional de la anterior
 * (CLASE_PROG, CMIMETIKA, OPT_FRMX…) sobreviviría en `listOJOIN` y viajaría a
 * SAP con la lente nueva.
 *
 * Se llama justo ANTES de recargar `datosAdicionales`, pasándole el array que
 * todavía corresponde a la lente saliente.
 *
 * Los campos se vacían en vez de borrarse: el BAPI espera la clave presente
 * (mismo criterio que `mapGraduacionToBAPI` con ESFERA/CILINDRO vacíos).
 *
 * @param {object} pedido            BAPI del pedido (se muta).
 * @param {Array}  datosAdicionales  Definición de adicionales de la lente saliente.
 * @returns {string[]} Campos efectivamente vaciados (para logs/tests).
 */
export function clearAdicionalesFromPedido(pedido, datosAdicionales) {
  if (!Array.isArray(datosAdicionales) || datosAdicionales.length === 0) return [];

  const ojos = pedido?.listOJOIN?.clsZES_OJO_INPUT;
  if (!Array.isArray(ojos) || ojos.length === 0) return [];

  const campos = [
    ...new Set(
      datosAdicionales
        .filter((d) => d?.BAPI_STRUCT === "ZES_OJO_INPUT" && d?.BAPI_CAMPO)
        .map((d) => d.BAPI_CAMPO),
    ),
  ];

  const vaciados = new Set();
  for (const ojo of ojos) {
    if (!ojo) continue;
    for (const campo of campos) {
      if (ojo[campo] !== undefined) {
        ojo[campo] = "";
        vaciados.add(campo);
      }
    }
  }

  return [...vaciados];
}

/**
 * Mapea datos adicionales (campos dinámicos) al formato BAPI.
 * Incluye manejo especial para OPT_FRMX, CLASE_PROG, TIPO_PROG, VISUAL_PREF.
 */
export function mapAdicionalesToBAPI(state, ojoInDch, ojoInIzq, pedido) {
  if (state.oi?.adicionales) {
    state.oi.adicionales.forEach((d) => {
      if (d.valor !== undefined) {
        ojoInIzq[d.BAPI_CAMPO] = d.valor;
      }
    });
  }

  if (state.od?.adicionales) {
    state.od.adicionales.forEach((d) => {
      if (d.valor !== undefined) {
        ojoInDch[d.BAPI_CAMPO] = d.valor;
      }
    });
  }

  if (state.adicionales) {
    state.adicionales.forEach((d) => {
      if (d.valor !== undefined) {
        ojoInDch[d.BAPI_CAMPO] = d.valor;
        ojoInIzq[d.BAPI_CAMPO] = d.valor;
      }
    });
  }

  // Manejo especial OPT_FRMX (optimización máxima)
  _processOptFrmx(state, ojoInDch, ojoInIzq, pedido);

  // ISOTIPO: asegurar "X"/"" en ambos ojos (ojoInDerecho/ojoInIzquierdo)
  _processIsotipo(state, ojoInDch, ojoInIzq);

  // Campos compartidos que deben estar en ambos ojos
  _syncSharedAdicionales(state, ojoInDch, ojoInIzq);
}

/**
 * @private Procesa OPT_FRMX (checkbox de optimización máxima)
 */
function _processOptFrmx(state, ojoInDch, ojoInIzq, pedido) {
  const optFrmxField =
    state?.adicionales?.find((d) => d.BAPI_CAMPO === "OPT_FRMX") ||
    state.od?.adicionales?.find((d) => d.BAPI_CAMPO === "OPT_FRMX") ||
    state.oi?.adicionales?.find((d) => d.BAPI_CAMPO === "OPT_FRMX");

  if (!optFrmxField) return;

  const isChecked =
    optFrmxField.valor === true || optFrmxField.valor === "S" || optFrmxField.valor === "X";

  if (isChecked) {
    ojoInDch.OPT_FRMX = "X";
    ojoInIzq.OPT_FRMX = "X";
    pedido.bEsMaxima = true;
    pedido.bNOEsMaxima = false;
    if (pedido.listCABIN?.clsZES_CAB_INPUT?.[0]) {
      pedido.listCABIN.clsZES_CAB_INPUT[0].MOD_ESPESORES = "X";
    }
  } else {
    ojoInDch.OPT_FRMX = "";
    ojoInIzq.OPT_FRMX = "";
    pedido.bEsMaxima = false;
    pedido.bNOEsMaxima = true;
  }
}

/**
 * @private Asegura ISOTIPO en ambos ojos como "X" o ""
 */
function _processIsotipo(state, ojoInDch, ojoInIzq) {
  const isotipoField =
    state?.adicionales?.find((d) => d.BAPI_CAMPO === "ISOTIPO") ||
    state.od?.adicionales?.find((d) => d.BAPI_CAMPO === "ISOTIPO") ||
    state.oi?.adicionales?.find((d) => d.BAPI_CAMPO === "ISOTIPO");

  const isChecked =
    isotipoField?.valor === "X" || isotipoField?.valor === true || isotipoField?.valor === "S";

  const valor = isChecked ? "X" : "";
  ojoInDch.ISOTIPO = valor;
  ojoInIzq.ISOTIPO = valor;
}

/**
 * @private Sincroniza campos compartidos (CLASE_PROG, TIPO_PROG, VISUAL_PREF) en ambos ojos
 */
function _syncSharedAdicionales(state, ojoInDch, ojoInIzq) {
  const camposCompartidos = ["CLASE_PROG", "TIPO_PROG", "VISUAL_PREF"];

  camposCompartidos.forEach((campo) => {
    // Buscar en adicionales compartidos
    const campoCompartido = state?.adicionales?.find((d) => d.BAPI_CAMPO === campo);
    if (campoCompartido?.valor !== undefined) {
      ojoInDch[campo] = campoCompartido.valor;
      ojoInIzq[campo] = campoCompartido.valor;
    } else {
      // Si no está en compartidos, buscar en OD o OI y copiar a ambos
      const campoOD = state.od?.adicionales?.find((d) => d.BAPI_CAMPO === campo);
      const campoOI = state.oi?.adicionales?.find((d) => d.BAPI_CAMPO === campo);
      const valor = campoOD?.valor ?? campoOI?.valor;
      if (valor !== undefined) {
        ojoInDch[campo] = valor;
        ojoInIzq[campo] = valor;
      }
    }
  });
}

// ─────────────────────────────────────────────
// Mapeo de suplementos (prismas, descentramientos, bases)
// ─────────────────────────────────────────────

/**
 * Mapea todos los suplementos (prismas, descentramientos, bases) al BAPI.
 */
export function mapSuplementosToBAPI(state, ojoInDch, ojoInIzq) {
  // Prismas
  _mapPrisma(state.od, ojoInDch, 1);
  _mapPrisma(state.od, ojoInDch, 2);
  _mapPrisma(state.oi, ojoInIzq, 1);
  _mapPrisma(state.oi, ojoInIzq, 2);

  // Descentramientos
  _mapDescentramiento(state.od, ojoInDch);
  _mapDescentramiento(state.oi, ojoInIzq);

  // Bases especiales
  _mapBaseEspecial(state.od, ojoInDch);
  _mapBaseEspecial(state.oi, ojoInIzq);
}

/**
 * @private Mapea datos de un prisma (1 o 2) al BAPI
 */
function _mapPrisma(eyeState, ojoIn, prismaNum) {
  const key = `prisma${prismaNum}`;
  const prisma = eyeState?.[key];
  if (!prisma) return;

  const suffix = prismaNum === 1 ? "PR1" : "PR2";

  ojoIn[`DIOP_${suffix}`] = !isEmpty(prisma.dioptrias) ? prisma.dioptrias : "";
  ojoIn[`BASE_${suffix}`] = !isEmpty(prisma.base) ? prisma.base : "";
  ojoIn[`GRAD_${suffix}`] = !isEmpty(prisma.grados) ? prisma.grados : "";
}

/**
 * @private Mapea descentramiento de un ojo al BAPI
 */
function _mapDescentramiento(eyeState, ojoIn) {
  if (!isEmpty(eyeState?.descentramientoSelected)) {
    ojoIn.COTA_DESN = String(eyeState.descentramientoSelected);
    ojoIn.SIGNO_BEN = "+";
  } else {
    ojoIn.COTA_DESN = "";
    ojoIn.SIGNO_BEN = "";
  }
}

/**
 * @private Mapea base especial de un ojo al BAPI
 */
function _mapBaseEspecial(eyeState, ojoIn) {
  if (isEmpty(eyeState?.baseSelected)) {
    ojoIn.VALOR_BEN = "";
    ojoIn.BASE = "";
    return;
  }

  const selectedBase = eyeState?.bases?.find((base) => base.BASE_NOMINAL === eyeState.baseSelected);

  if (selectedBase) {
    ojoIn.VALOR_BEN = String(selectedBase.VALOR_BASE || "");
    ojoIn.BASE =
      selectedBase.BASE_CON_SIGNO ||
      `${selectedBase.SIGNO_BASE || ""}${selectedBase.VALOR_BASE || ""}`;
  } else {
    ojoIn.VALOR_BEN = "";
    ojoIn.BASE = "";
  }
}

// ─────────────────────────────────────────────
// Mapeo de cabecera del pedido
// ─────────────────────────────────────────────

/**
 * Mapea datos de la cabecera del pedido (VKORG, referencia, bisel, montaje, etc.)
 */
export function mapCabeceraToBAPI(pedido, state, rootState) {
  const profile = rootState.indo.profile;
  const areaCliente = rootState.gama.areaCliente;

  // VKORG
  pedido.VKORG = areaCliente?.ORG_VENTA
    ? areaCliente.ORG_VENTA.padStart(4, "0")
    : profile.AreaVenta.ORG_VENTA.padStart(4, "0");

  if (!pedido.listCABIN || pedido.listCABIN === "") {
    pedido.listCABIN = { clsZES_CAB_INPUT: [{}] };
  } else if (Array.isArray(pedido.listCABIN)) {
    pedido.listCABIN = {
      clsZES_CAB_INPUT: pedido.listCABIN.length ? [...pedido.listCABIN] : [{}],
    };
  } else if (!pedido.listCABIN.clsZES_CAB_INPUT?.length) {
    pedido.listCABIN.clsZES_CAB_INPUT = [{}];
  }

  const cabin = pedido.listCABIN.clsZES_CAB_INPUT[0];
  cabin.VKORG = pedido.VKORG;
  cabin.ACEPTA_DUPLI = "";

  // Referencia
  pedido.REF_PEDIDO = state.referencia;

  // BISEL y MONTAJE (mantener valor del pedido si ya existe)
  if (cabin.BISEL === undefined || cabin.BISEL === null) {
    cabin.BISEL = state.bisel ? "X" : "";
  }
  if (cabin.MONTAJE === undefined || cabin.MONTAJE === null) {
    cabin.MONTAJE = state.montaje ? "X" : "";
  }

  // Flags fijos
  cabin.AUGRU = "INET";
  cabin.FLAG_ONES = "X";

  // Descripción de organización
  const vkorgDescr =
    areaCliente?.TEXTO_ORGANIZACION?.toUpperCase() ||
    profile.AreaVenta?.TEXTO_ORGANIZACION?.toUpperCase() ||
    "";

  cabin.REF_CLIENT = state.referencia;
  cabin.VKORG_DESCR = vkorgDescr;
  cabin.CODI_PROM = state.codigoPromocional || "";

  // CHKB_POS_USO
  const tipoPosUso = profile.DatosConfig.CONFIGURACION.TIPO_POS_USO;
  cabin.CHKB_POSUSO = tipoPosUso === "" || tipoPosUso === "1" ? "X" : "";
}

/**
 * Aplica `others.lensesType` (forma, medidas, DNP) al BAPI antes de preparar/calcular.
 * Sin esto, `preparePedido` solo copia `indo.pedido` y puede enviar CABFRM vacío
 * aunque la UI tenga datos en el store `others`.
 */
export function applyLensesTypeToPedidoCabfrm(pedido, lensesType) {
  if (!pedido || !lensesType) return;

  if (!pedido.listCABFRM) pedido.listCABFRM = {};
  if (!pedido.listCABFRM.clsZES_CAB_FORMA) pedido.listCABFRM.clsZES_CAB_FORMA = [{ ORIGEN_DATOS: "" }];
  if (!pedido.listCABFRM.clsZES_CAB_FORMA[0]) pedido.listCABFRM.clsZES_CAB_FORMA[0] = { ORIGEN_DATOS: "" };

  const cab = pedido.listCABFRM.clsZES_CAB_FORMA[0];

  const pick = (ltVal, fallback) => {
    if (ltVal === null || ltVal === undefined) return fallback ?? "";
    const s = String(ltVal).trim();
    return s !== "" ? String(ltVal).replace(",", ".") : (fallback ?? "");
  };

  const od = lensesType.od || {};
  const oi = lensesType.oi || {};

  cab.FORMA_BASICA =
    lensesType.type !== undefined && lensesType.type !== null
      ? Number(lensesType.type) + 1
      : cab.FORMA_BASICA !== undefined && cab.FORMA_BASICA !== null && cab.FORMA_BASICA !== ""
        ? Number(cab.FORMA_BASICA)
        : 0;

  cab.CALIBRE = pick(lensesType.calibre, cab.CALIBRE);
  cab.PUENTE = pick(lensesType.puente, cab.PUENTE);
  cab.ALT_BOX = pick(lensesType.altura_boxing, cab.ALT_BOX);
  cab.DIST_NP_OD = pick(od.dnp, cab.DIST_NP_OD);
  cab.DIST_NP_OI = pick(oi.dnp, cab.DIST_NP_OI);
  cab.ALTURA_OD = pick(od.altura, cab.ALTURA_OD);
  cab.ALTURA_OI = pick(oi.altura, cab.ALTURA_OI);

  if (lensesType.forma && lensesType.forma !== "0") {
    cab.ORIGEN_DATOS = String(lensesType.forma);
  }

  const ojos = pedido.listOJOIN?.clsZES_OJO_INPUT;
  if (!Array.isArray(ojos)) return;

  const ojoD = ojos.find((o) => o.ZOJO === "D");
  const ojoI = ojos.find((o) => o.ZOJO === "I");
  if (ojoD) {
    ojoD.DIST_NP = pick(od.dnp, ojoD.DIST_NP);
    ojoD.CALIBRE = pick(lensesType.calibre, ojoD.CALIBRE);
    ojoD.PUENTE = pick(lensesType.puente, ojoD.PUENTE);
    ojoD.PUENTEORIG = pick(lensesType.puente, ojoD.PUENTEORIG);
    ojoD.ALT_ARO = pick(od.altura, ojoD.ALT_ARO);
  }
  if (ojoI) {
    ojoI.DIST_NP = pick(oi.dnp, ojoI.DIST_NP);
    ojoI.CALIBRE = pick(lensesType.calibre, ojoI.CALIBRE);
    ojoI.PUENTE = pick(lensesType.puente, ojoI.PUENTE);
    ojoI.PUENTEORIG = pick(lensesType.puente, ojoI.PUENTEORIG);
    ojoI.ALT_ARO = pick(oi.altura, ojoI.ALT_ARO);
  }
}

/**
 * Sincroniza datos de altura y DNP desde CABFRM a los ojos del pedido.
 */
export function syncAlturaAndDNP(pedido, ojoInDch, ojoInIzq) {
  const cabForma = pedido.listCABFRM?.clsZES_CAB_FORMA?.[0];
  if (!cabForma) return;

  if (cabForma.ALTURA_OD) ojoInDch.ALT_ARO = cabForma.ALTURA_OD;
  if (cabForma.ALTURA_OI) ojoInIzq.ALT_ARO = cabForma.ALTURA_OI;
  if (cabForma.DIST_NP_OD) ojoInDch.DIST_NP = cabForma.DIST_NP_OD;
  if (cabForma.DIST_NP_OI) ojoInIzq.DIST_NP = cabForma.DIST_NP_OI;
}

/**
 * Monta los ojos procesados de vuelta en la estructura del pedido.
 */
export function mountEyesIntoPedido(pedido, ojoInDch, ojoInIzq) {
  if (!pedido.listOJOIN) {
    pedido.listOJOIN = {};
  }
  pedido.listOJOIN.clsZES_OJO_INPUT = [{ ...ojoInDch }, { ...ojoInIzq }];

  // Asignar también a propiedades auxiliares si existen
  if (pedido.ojoInDerecho) {
    Object.assign(pedido.ojoInDerecho, ojoInDch);
  }
  if (pedido.ojoInIzquierdo) {
    Object.assign(pedido.ojoInIzquierdo, ojoInIzq);
  }
}
